import React from 'react';
import { Box, Chip, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import CategoryIcon from '@mui/icons-material/Category';
import LabelIcon from '@mui/icons-material/LabelOutlined';
import LayersIcon from '@mui/icons-material/LayersOutlined';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOnOutlined';
import NotificationsIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CampaignIcon from '@mui/icons-material/CampaignOutlined';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import StartIcon from '@mui/icons-material/StartOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulletedOutlined';

import { adminCatalogApi, CatalogSlug } from '../../../api/adminCatalogApi';
import { paths } from '../../../routes/paths';
import { colors, typo } from '../../../theme/tokens';

/* ------------------------------------------------------------------ */
/*  Catalog registry                                                   */
/* ------------------------------------------------------------------ */

interface CatalogEntry {
  key: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ sx?: any }>;
  /** Non-null = live in Round 2; null = coming soon. */
  slug: CatalogSlug | null;
  path?: string;
}

const CATALOGS: CatalogEntry[] = [
  {
    key: 'task-types',
    label: 'Task types',
    description: 'FK parent for task.task_type_code.',
    icon: LabelIcon,
    slug: 'task-types',
    path: paths.admin.catalogs.taskTypes(),
  },
  {
    key: 'task-categories',
    label: 'Task categories',
    description: 'FK parent for task.task_category_code.',
    icon: CategoryIcon,
    slug: 'task-categories',
    path: paths.admin.catalogs.taskCategories(),
  },
  {
    key: 'task-reward-types',
    label: 'Task reward types',
    description: 'Reward value types (dollars, entries, points).',
    icon: MonetizationOnIcon,
    slug: 'task-reward-types',
    path: paths.admin.catalogs.taskRewardTypes(),
  },
  {
    key: 'component-types',
    label: 'Component types',
    description: 'CMS component kinds (schema_json defines prop surface).',
    icon: LayersIcon,
    slug: 'component-types',
    path: paths.admin.catalogs.componentTypes(),
  },
  {
    key: 'notification-channels',
    label: 'Notification channels',
    description: 'Email / SMS / push.',
    icon: NotificationsIcon,
    slug: 'notification-channels',
    path: paths.admin.catalogs.notificationChannels(),
  },
  {
    key: 'notification-categories',
    label: 'Notification categories',
    description: 'Transactional vs marketing groupings.',
    icon: CampaignIcon,
    slug: 'notification-categories',
    path: paths.admin.catalogs.notificationCategories(),
  },
  {
    key: 'wallet-types',
    label: 'Wallet types',
    description: 'Reward / funding / master wallets.',
    icon: AccountBalanceWalletIcon,
    slug: 'wallet-types',
    path: paths.admin.catalogs.walletTypes(),
  },
  {
    key: 'onboarding-components',
    label: 'Onboarding components',
    description: 'Enumerated onboarding step kinds (schema_json defines step config).',
    icon: StartIcon,
    slug: 'onboarding-components',
    path: paths.admin.catalogs.onboardingComponents(),
  },
  {
    key: 'canonical-fields',
    label: 'Canonical fields',
    description: 'Cross-tenant field registry.',
    icon: TuneIcon,
    slug: 'canonical-fields',
    path: paths.admin.catalogs.canonicalFields(),
  },
  {
    key: 'cohort-attribute-defs',
    label: 'Cohort attributes',
    description: 'Registered cohort attribute keys.',
    icon: FormatListBulletedIcon,
    slug: 'cohort-attribute-defs',
    path: paths.admin.catalogs.cohortAttributeDefs(),
  },
];

/* ------------------------------------------------------------------ */
/*  Row                                                                */
/* ------------------------------------------------------------------ */

const CountPill: React.FC<{ count: number | null; empty: boolean }> = ({ count, empty }) => {
  if (count == null) return null;
  return (
    <Box
      sx={{
        fontSize: 11,
        fontWeight: 700,
        fontFamily: 'Manrope, sans-serif',
        letterSpacing: '0.4px',
        textTransform: 'uppercase',
        color: empty ? colors.warningText : colors.adminAccent,
        backgroundColor: empty ? colors.warningBgStrong : colors.adminAccentSubtle,
        borderRadius: '4px',
        px: 1,
        py: 0.25,
        flexShrink: 0,
      }}
    >
      {count} {count === 1 ? 'row' : 'rows'}
    </Box>
  );
};

const CatalogRowItem: React.FC<{ entry: CatalogEntry; isLast: boolean }> = ({ entry, isLast }) => {
  const navigate = useNavigate();
  const enabled = !!entry.slug && !!entry.path;

  const countQuery = useQuery({
    queryKey: ['admin', 'catalogs', entry.slug, 'count'],
    queryFn: async () => {
      if (!entry.slug) return 0;
      const rows = await adminCatalogApi.list(entry.slug);
      return rows.length;
    },
    enabled,
    staleTime: 30_000,
  });

  const count = countQuery.data;
  const isEmpty = count === 0;
  const Icon = entry.icon;

  const rowSx: any = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    px: 2.5,
    py: 1.75,
    borderBottom: isLast ? 'none' : `1px solid ${colors.borderLight}`,
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.6,
    // Dashed border + amber hover signals an empty catalog (per Designer B).
    ...(isEmpty && enabled
      ? {
          borderLeft: `2px dashed ${colors.warningBorder}`,
        }
      : {}),
    '&:hover': enabled
      ? { backgroundColor: colors.bgSubtle }
      : { backgroundColor: 'transparent' },
  };

  const content = (
    <Box
      sx={rowSx}
      onClick={() => {
        if (enabled && entry.path) navigate(entry.path);
      }}
      role={enabled ? 'button' : undefined}
      tabIndex={enabled ? 0 : -1}
      onKeyDown={(e) => {
        if (!enabled || !entry.path) return;
        if (e.key === 'Enter' || e.key === ' ') navigate(entry.path);
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '8px',
          backgroundColor: colors.adminAccentSubtle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ color: colors.adminAccent, fontSize: 20 }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 600,
            fontSize: 14,
            color: colors.textPrimary,
          }}
        >
          {entry.label}
        </Typography>
        <Typography sx={{ ...typo.secondaryText, fontSize: 13 }}>{entry.description}</Typography>
      </Box>

      {enabled ? (
        countQuery.isLoading ? (
          <CircularProgress size={14} sx={{ color: colors.adminAccent }} />
        ) : (
          <CountPill count={count ?? 0} empty={isEmpty} />
        )
      ) : (
        <Chip
          size="small"
          label="Coming soon"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            backgroundColor: colors.bgMuted,
            color: colors.textTertiary,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
          }}
        />
      )}
    </Box>
  );

  if (!enabled) {
    return (
      <Tooltip title="Coming soon" placement="left">
        <span style={{ display: 'block' }}>{content}</span>
      </Tooltip>
    );
  }
  return content;
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const CatalogsLanding: React.FC = () => {
  return (
    <Box sx={{ maxWidth: 1120 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: colors.textTertiary,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            mb: 0.5,
          }}
        >
          Admin
        </Typography>
        <Typography sx={{ ...typo.pageTitle, mb: 0.5 }}>Reference Catalogs</Typography>
        <Typography sx={{ ...typo.pageSubtitle }}>
          Lookup tables that back the Global Catalog entities. Round 4 adds task-reward-types
          and wallet-types; remaining catalogs land in Round 5.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${colors.borderLight}`,
          backgroundColor: colors.bgWhite,
          overflow: 'hidden',
        }}
      >
        {CATALOGS.map((entry, idx) => (
          <CatalogRowItem
            key={entry.key}
            entry={entry}
            isLast={idx === CATALOGS.length - 1}
          />
        ))}
      </Paper>
    </Box>
  );
};

export default CatalogsLanding;
